import type { DozerConfig, VehicleBox } from '../config/dozer.config';
import type { PlayerConfig } from '../config/player.config';
import type { LaneStretch } from '../hazards/hazardRules';
import type { ObstacleDefinition } from '../obstacles/ObstacleDefinition';
import { ObstacleType } from '../obstacles/ObstacleType';
import type { JumpPhysics } from '../physics/JumpPhysics';
import type { TimeWindow } from '../physics/JumpReach';
import type { VehicleSeat } from '../player/VehicleSeat';
import type { FixedObstaclePlacement, GroundGap, ObstacleSpawnPoint, SegmentDefinition } from '../level/segments/SegmentDefinition';

/**
 * Static facts of the dozer set piece (SEG_DOZER_60M), shared by the DozerManager, the segment validator, the
 * populator (moduleStretches) and the tests – pure functions of the data, no state.
 */

const EPSILON = 1e-9;

/** Extent of the dozer's colliders along its length (vehicle space): `rear` (negative) and `front` (blade). */
export function dozerSpan(colliders: readonly VehicleBox[]): { readonly rear: number; readonly front: number } {
  let rear = Infinity;
  let front = -Infinity;
  for (const c of colliders) {
    rear = Math.min(rear, c.center.z - c.size.z / 2);
    front = Math.max(front, c.center.z + c.size.z / 2);
  }
  return { rear, front };
}

/**
 * Lowest feet height at which a runner who touches the parked dozer gets on: within one step of the track top –
 * the lowest part it lands on (track, fender, seat). Lower, the dozer is in its way: a frontal hit on foot.
 */
export function mountMinFeet(config: DozerConfig, player: PlayerConfig): number {
  return config.vehicle.trackTop - player.maxStepHeight;
}

/**
 * Arrival times at the dozer (s after take-off) at which a single jump's feet are at least `minFeet` high – the
 * runner gets on if its front meets the dozer then. Same model as physics/JumpReach.ts: fixed-step integration
 * loses g·dt/2 of the launch speed. Null if a single jump never gets that high.
 */
export function mountArrivalWindow(physics: JumpPhysics, minFeet: number, fixedStep = 0): TimeWindow | null {
  const g = physics.gravity;
  const v0 = physics.jumpVelocity - (g * fixedStep) / 2;
  // v0 t − g t²/2 = minFeet
  const discriminant = v0 * v0 - 2 * g * minFeet;
  if (discriminant < 0) return null;
  const root = Math.sqrt(discriminant);
  return { from: (v0 - root) / g, to: (v0 + root) / g };
}

/**
 * Seconds of take-off timing that still get the runner onto the dozer at `speed` with `runUp` m of free road
 * (front of the runner → rear face of the dozer): take-off points with an arrival time inside the window.
 */
export function mountTakeOffWindow(window: TimeWindow | null, runUp: number, speed: number): number {
  if (!window || speed <= 0) return 0;
  return Math.max(0, Math.min(window.to, runUp / speed) - window.from);
}

/** The seat as the PlayerController rides it (DRIVING): pace, speed, the vehicle's body and the throw-off of a hit. */
export function dozerSeat(config: DozerConfig, player: PlayerConfig): VehicleSeat {
  const span = dozerSpan(config.vehicle.colliders);
  const seat = config.vehicle.seat;
  const front = span.front - seat.z;
  return {
    seatHeight: seat.y,
    speedFactor: config.drive.speedFactor,
    laneChangeDuration: config.drive.laneChangeDuration,
    body: { width: config.drive.body.width, height: config.drive.body.height, rear: seat.z - span.rear, front },
    // past the blade by half a body, the hit's push back and the clearance (the obstacle that stopped the machine)
    throwOff: { ahead: front + player.collider.depth / 2 + player.hitPushBack + config.throwOff.clearance, launchSpeed: config.throwOff.launchSpeed },
  };
}

/**
 * Vertical launch speed that carries a rider leaving the seat (`seatHeight` above the road) `distance` m forward at
 * the run speed `speed` before its feet are back at road level – over the pit when the earth was not enough.
 * Never less than a ground jump. Fixed-step integration (JumpReach) loses g·dt/2 – added back.
 */
export function catapultSpeed(physics: JumpPhysics, seatHeight: number, distance: number, speed: number, fixedStep = 0): number {
  if (speed <= 0 || distance <= 0) return physics.jumpVelocity;
  return Math.max(physics.jumpVelocity, launchToLand(physics, seatHeight, distance, speed, fixedStep));
}

/**
 * Vertical launch speed that brings a rider leaving the seat (`seatHeight` above the road) down to road level
 * exactly `distance` m ahead at `speed` (fixed-step loss g·dt/2 added back, like JumpReach). May be negative: even
 * stepping off lands further.
 */
export function launchToLand(physics: JumpPhysics, seatHeight: number, distance: number, speed: number, fixedStep = 0): number {
  const t = distance / speed;
  const g = physics.gravity;
  // seatHeight + (v − g·dt/2)·t − g·t²/2 = 0
  return (g * t) / 2 - seatHeight / t + (g * fixedStep) / 2;
}

/**
 * The hop off at the end of the ride: lands `distance` m ahead at the latest – never higher than a normal jump,
 * never pushed down (a launch of 0 = stepping off the seat).
 */
export function finishHopSpeed(physics: JumpPhysics, seatHeight: number, distance: number, speed: number, fixedStep = 0): number {
  if (speed <= 0 || distance <= 0) return 0;
  return Math.min(physics.jumpVelocity, Math.max(0, launchToLand(physics, seatHeight, distance, speed, fixedStep)));
}

/** The parked dozer's spawn point and the pit of a dozer module (undefined parts: the validator reports them). */
export function dozerParts(definition: SegmentDefinition): { readonly point?: ObstacleSpawnPoint; readonly pit?: GroundGap } {
  const setPiece = definition.dozer;
  if (!setPiece) return {};
  return {
    point: definition.obstacleSpawnPoints.find((p) => p.id === setPiece.spawnPointId),
    pit: definition.terrain?.gaps.find((g) => g.id === setPiece.pitGapId),
  };
}

/**
 * What the parked dozer keeps busy in its lane (segment-local): its own approach from the module start up to its
 * blade – a runner coming off a container roof of the module before must not land in its run-up or in it
 * (roofRunUp.moduleStretches, SegmentPopulator: drop zones never reach it).
 */
export function dozerStretch(definition: SegmentDefinition, config: DozerConfig): LaneStretch | null {
  const { point } = dozerParts(definition);
  if (!point) return null;
  return { lane: point.lane, zFrom: 0, zTo: point.position.z + dozerSpan(config.vehicle.colliders).front };
}

// ── Driving layout ───────────────────────────────────────────────────────────

/** One decision line of a layout as the driver sees it: hard obstacles block their lane, heaps are free (pushed). */
export interface DrivingRow {
  /** Where the blade meets the row: the nearest front face of its obstacles (segment-local z). */
  readonly z: number;
  readonly lanes: readonly (ObstacleType | null)[];
  /** Lanes with a heap of earth. */
  readonly heaps: readonly number[];
}

/**
 * The rows of a layout for a driver (everything ahead of the parked dozer): heaps (DozerConfig.heapObstacleId) are
 * pushed – free; anything else stops the machine – LANE_CHANGE (the dozer cannot jump). Sorted along the module.
 */
export function drivingRows(
  definition: SegmentDefinition,
  placements: readonly FixedObstaclePlacement[],
  obstacleOf: (id: string) => ObstacleDefinition | undefined,
  config: DozerConfig,
): DrivingRow[] {
  const laneCount = definition.lanes.length;
  const byRow = new Map<string, { z: number; lanes: (ObstacleType | null)[]; heaps: number[] }>();
  for (const placement of placements) {
    if (placement.spawnPointId === definition.dozer?.spawnPointId) continue;
    const point = definition.obstacleSpawnPoints.find((p) => p.id === placement.spawnPointId);
    const obstacle = obstacleOf(placement.obstacleId);
    if (!point || !obstacle) continue;
    const face = point.position.z + Math.min(...obstacle.colliders.map((c) => c.center.z - c.size.z / 2));
    const row = byRow.get(point.row) ?? { z: Infinity, lanes: Array.from({ length: laneCount }, () => null), heaps: [] };
    row.z = Math.min(row.z, face);
    if (obstacle.id === config.heapObstacleId) row.heaps.push(point.lane);
    else row.lanes[point.lane] = ObstacleType.LaneChange;
    byRow.set(point.row, row);
  }
  return [...byRow.values()].sort((a, b) => a.z - b.z);
}

/** A driver's way through the heaps: the lane per row and whether reaction + steering fit between the rows. */
export interface DriveRoute {
  readonly lanes: readonly number[];
  readonly feasible: boolean;
}

/**
 * The route that pushes every heap (one heap per row, else the row's first heap): from `startLane` with the blade at
 * `startZ`, at `speed` – a lane change needs the reaction time plus `laneChangeDuration` per lane, a lane with a
 * hard obstacle is never the route, and a change of more than one lane must not sweep through a hard obstacle.
 */
export function heapRoute(rows: readonly DrivingRow[], startLane: number, startZ: number, speed: number, laneChangeDuration: number, reactionTime: number): DriveRoute {
  const lanes: number[] = [];
  let lane = startLane;
  let z = startZ;
  let feasible = true;
  for (const row of rows) {
    const target = row.heaps[0] ?? lane;
    const steps = Math.abs(target - lane);
    const available = (row.z - z) / Math.max(speed, EPSILON);
    if (steps > 0 && reactionTime + steps * laneChangeDuration > available + EPSILON) feasible = false;
    if (row.lanes[target] !== null && row.lanes[target] !== undefined) feasible = false;
    lanes.push(target);
    lane = target;
    z = row.z;
  }
  return { lanes, feasible };
}
